/**
 * MS604 / MS594 — client-side fulfilment policy for product add-ons.
 * Mirrors docs/audits/MS594-addon-fulfilment-registry.md Types A–F.
 */

export type FulfilmentMode =
  | "co_deliver"
  | "stackable_voucher"
  | "upgrade_only"
  | "gift_subscription"
  | "blocked";

const FULFILMENT_MODES: ReadonlySet<string> = new Set([
  "co_deliver",
  "stackable_voucher",
  "upgrade_only",
  "gift_subscription",
  "blocked",
]);

export function isGiftSubscriptionSku(sku: {
  name?: string | null;
  slug?: string | null;
}): boolean {
  const hay = `${sku.name ?? ""} ${sku.slug ?? ""}`.toLowerCase();
  return (
    hay.includes("subscription") ||
    hay.includes("prepaid") ||
    hay.includes("gift sub")
  );
}

function parseFulfilmentMode(raw: string | null | undefined): FulfilmentMode | null {
  if (raw && FULFILMENT_MODES.has(raw)) return raw as FulfilmentMode;
  return null;
}

/** Effective mode: DB column when set, else legacy heuristics. */
export function effectiveFulfilmentMode(sku: {
  fulfilment_mode?: string | null;
  name?: string | null;
  slug?: string | null;
  is_voucher?: boolean;
}): FulfilmentMode {
  const explicit = parseFulfilmentMode(sku.fulfilment_mode);
  if (explicit) return explicit;
  if (isGiftSubscriptionSku(sku)) return "gift_subscription";
  return sku.is_voucher ? "stackable_voucher" : "co_deliver";
}

function baseModeAllowsAddons(mode: FulfilmentMode): boolean {
  return mode !== "gift_subscription" && mode !== "upgrade_only" && mode !== "blocked";
}

function addonModeCompatibleWithBase(
  baseMode: FulfilmentMode,
  addonMode: FulfilmentMode,
): boolean {
  if (addonMode === "gift_subscription" || addonMode === "upgrade_only" || addonMode === "blocked") {
    return false;
  }
  if (baseMode === "stackable_voucher") return addonMode === "stackable_voucher";
  if (baseMode === "co_deliver") return addonMode === "co_deliver";
  return false;
}

/** Whether the base SKU may offer optional add-ons in ProductDetailModal. */
export function baseSkuAllowsAddons(sku: {
  fulfilment_mode?: string | null;
  name?: string | null;
  slug?: string | null;
  is_voucher?: boolean;
}): boolean {
  return baseModeAllowsAddons(effectiveFulfilmentMode(sku));
}

/** Filter linked add-ons to those valid for the active base SKU. */
export function filterAddonsForBase<
  T extends {
    sku_id: string;
    name: string;
    slug?: string | null;
    is_voucher?: boolean;
    fulfilment_mode?: string | null;
  },
>(
  base: {
    fulfilment_mode?: string | null;
    name?: string | null;
    slug?: string | null;
    is_voucher?: boolean;
  },
  addons: T[],
): T[] {
  if (!baseSkuAllowsAddons(base)) return [];

  const baseMode = effectiveFulfilmentMode(base);
  return addons.filter((addon) => addonModeCompatibleWithBase(baseMode, effectiveFulfilmentMode(addon)));
}
